class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.loading = false;
    this.hasMorePages = true;
    this.productGrid = null;
    this.paginationWrapper = null;
    this.sentinel = null;
    this.observer = null;
  }

  connectedCallback() {
    // Esperar a que el DOM esté completamente listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.setup(), 100);
      });
    } else {
      setTimeout(() => this.setup(), 100);
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.observer && this.sentinel) {
      this.observer.unobserve(this.sentinel);
      this.observer = null;
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.remove();
      this.sentinel = null;
    }
  }

  setup() {
    this.productGrid = document.getElementById('product-grid');
    this.paginationWrapper = document.querySelector('.pagination-wrapper');
    
    if (!this.productGrid) {
      console.warn('InfiniteScroll: No se encontró el elemento #product-grid');
      return;
    }

    // Obtener página actual de la paginación o de la URL
    if (this.paginationWrapper) {
      this.currentPage = parseInt(this.paginationWrapper.dataset.page || '1');
      // Ocultar paginación tradicional
      this.paginationWrapper.style.display = 'none';
    } else {
      // Si no hay paginación visible, obtener de la URL
      const urlParams = new URLSearchParams(window.location.search);
      this.currentPage = parseInt(urlParams.get('page') || '1');
    }

    // Verificar si hay más páginas
    this.checkForMorePages();

    // Crear sentinel para detectar cuando el usuario hace scroll
    this.createSentinel();
    
    // Configurar observer
    this.setupIntersectionObserver();

    console.log('InfiniteScroll inicializado:', {
      currentPage: this.currentPage,
      hasMorePages: this.hasMorePages,
      sentinel: !!this.sentinel
    });
  }

  checkForMorePages() {
    // Verificar si hay botón de siguiente página
    // En Shopify, el botón "siguiente" tiene la clase pagination__item--prev
    const nextButton = this.paginationWrapper?.querySelector('.pagination__item--prev');
    this.hasMorePages = !!nextButton;
    
    // Si no hay paginación wrapper, verificar por el número de productos
    if (!this.paginationWrapper) {
      const productsPerPage = parseInt(this.productGrid.dataset.productsPerPage || '24');
      const currentProducts = this.productGrid.querySelectorAll('li.grid__item').length;
      // Si hay exactamente productsPerPage productos, probablemente hay más páginas
      this.hasMorePages = currentProducts >= productsPerPage;
    }
    
    if (!this.hasMorePages) {
      // Si no hay más páginas, ocultar el sentinel
      if (this.sentinel) {
        this.sentinel.style.display = 'none';
      }
      console.log('InfiniteScroll: No hay más páginas disponibles');
    }
  }

  createSentinel() {
    // Remover sentinel anterior si existe
    const existingSentinel = document.getElementById('infinite-scroll-sentinel');
    if (existingSentinel) {
      existingSentinel.remove();
    }

    // Crear elemento observador
    this.sentinel = document.createElement('div');
    this.sentinel.id = 'infinite-scroll-sentinel';
    this.sentinel.style.height = '20px';
    this.sentinel.style.width = '100%';
    this.sentinel.style.background = 'transparent';
    this.sentinel.style.position = 'relative';
    
    // Añadir después del grid, dentro del contenedor de la colección
    // El grid es un <ul>, así que el sentinel va después del </ul>
    const gridParent = this.productGrid.parentNode; // Esto es el div.collection
    if (gridParent) {
      // Insertar después del grid (ul)
      gridParent.insertBefore(this.sentinel, this.productGrid.nextSibling);
    }
    
    console.log('InfiniteScroll: Sentinel creado y posicionado después del grid');
  }

  setupIntersectionObserver() {
    if (!this.sentinel) {
      console.warn('InfiniteScroll: No se pudo crear el sentinel');
      return;
    }

    // Limpiar observer anterior
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        console.log('InfiniteScroll: IntersectionObserver entry:', {
          isIntersecting: entry.isIntersecting,
          loading: this.loading,
          hasMorePages: this.hasMorePages,
          intersectionRatio: entry.intersectionRatio
        });
        
        if (entry.isIntersecting && !this.loading && this.hasMorePages) {
          console.log('InfiniteScroll: Sentinel detectado, cargando siguiente página...');
          this.loadNextPage();
        }
      });
    }, {
      root: null,
      rootMargin: '300px', // Reducido a 300px para que sea más sensible
      threshold: 0
    });

    this.observer.observe(this.sentinel);
    console.log('InfiniteScroll: Observer configurado y observando sentinel', this.sentinel);
    
    // Verificar inmediatamente si el sentinel está visible
    const sentinelRect = this.sentinel.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const isVisible = sentinelRect.top < viewportHeight + 300;
    console.log('InfiniteScroll: Sentinel position:', {
      top: sentinelRect.top,
      viewportHeight: viewportHeight,
      isVisible: isVisible
    });
    
    // Si el sentinel ya está visible, cargar inmediatamente
    if (isVisible && !this.loading && this.hasMorePages) {
      console.log('InfiniteScroll: Sentinel ya visible, cargando primera página extra...');
      setTimeout(() => this.loadNextPage(), 500);
    }
    
    // Añadir listener de scroll como respaldo
    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  handleScroll() {
    if (this.loading || !this.hasMorePages || !this.sentinel) return;
    
    const sentinelRect = this.sentinel.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // Si el sentinel está a menos de 500px del viewport, cargar
    if (sentinelRect.top < viewportHeight + 500) {
      console.log('InfiniteScroll: Scroll detectado, sentinel cercano, cargando...');
      this.loadNextPage();
    }
  }

  async loadNextPage() {
    if (this.loading || !this.hasMorePages) {
      console.log('InfiniteScroll: No se puede cargar. Loading:', this.loading, 'HasMorePages:', this.hasMorePages);
      return;
    }

    console.log('InfiniteScroll: Iniciando carga de página', this.currentPage + 1);
    this.loading = true;
    this.showLoadingState();

    try {
      const nextPage = this.currentPage + 1;
      
      // Construir URL preservando parámetros actuales
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage);
      
      // Obtener section_id del grid
      const sectionId = this.productGrid.dataset.id;
      if (sectionId) {
        url.searchParams.set('section_id', sectionId);
      }

      // Hacer fetch de la siguiente página
      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Buscar el grid en el HTML recibido
      const productGridContainer = doc.getElementById('ProductGridContainer');
      if (!productGridContainer) {
        this.hasMorePages = false;
        this.hideLoadingState();
        return;
      }

      const newProductGrid = productGridContainer.querySelector('#product-grid');
      if (!newProductGrid) {
        this.hasMorePages = false;
        this.hideLoadingState();
        return;
      }

      // Extraer productos del nuevo HTML
      const newProducts = Array.from(newProductGrid.querySelectorAll('li.grid__item'));
      
      if (newProducts.length === 0) {
        this.hasMorePages = false;
        this.hideLoadingState();
        return;
      }

      // Calcular índice inicial para lazy loading
      const currentProductCount = this.productGrid.querySelectorAll('li.grid__item').length;
      
      // Añadir nuevos productos al grid
      newProducts.forEach((product, index) => {
        const clone = product.cloneNode(true);
        
        // Actualizar lazy loading para imágenes
        const images = clone.querySelectorAll('img');
        images.forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          // Asegurar lazy loading para productos después de los primeros
          if (currentProductCount + index >= 2) {
            img.loading = 'lazy';
          }
        });

        // Reinicializar scripts dentro del producto
        const scripts = clone.querySelectorAll('script');
        scripts.forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });

        // Añadir producto al grid
        this.productGrid.appendChild(clone);
      });

      // Actualizar página actual
      this.currentPage = nextPage;

      // Mover el sentinel después de los productos añadidos
      if (this.sentinel && this.sentinel.parentNode) {
        this.observer.unobserve(this.sentinel);
        this.sentinel.parentNode.removeChild(this.sentinel);
      }
      // Añadir el sentinel después del grid (que ahora tiene más productos)
      const gridParent = this.productGrid.parentNode; // div.collection
      if (gridParent) {
        gridParent.insertBefore(this.sentinel, this.productGrid.nextSibling);
      }

      // Verificar si hay más páginas
      const newPagination = productGridContainer.querySelector('.pagination-wrapper');
      const nextButton = newPagination?.querySelector('.pagination__item--prev');
      this.hasMorePages = !!nextButton;

      // Si no hay botón siguiente pero hay productos, seguir intentando
      if (!this.hasMorePages && newProducts.length > 0) {
        const productsPerPage = parseInt(this.productGrid.dataset.productsPerPage || '24');
        // Si cargamos una página completa, probablemente hay más
        this.hasMorePages = newProducts.length >= productsPerPage;
      }

      if (!this.hasMorePages && this.sentinel) {
        this.sentinel.style.display = 'none';
        console.log('InfiniteScroll: Última página alcanzada');
      } else {
        console.log('InfiniteScroll: Página cargada exitosamente. Productos añadidos:', newProducts.length);
        // Re-observar el sentinel después de moverlo
        if (this.observer && this.sentinel) {
          // Pequeño delay para asegurar que el DOM se actualizó
          setTimeout(() => {
            this.observer.observe(this.sentinel);
            console.log('InfiniteScroll: Sentinel re-observado después de cargar productos');
          }, 100);
        }
      }

      // Reinicializar animaciones si existen
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }

      // Disparar evento para otros scripts
      document.dispatchEvent(new CustomEvent('infiniteScrollLoaded', {
        detail: { page: nextPage, productsCount: newProducts.length }
      }));

    } catch (error) {
      console.error('Error cargando siguiente página:', error);
      this.hasMorePages = false;
    } finally {
      this.loading = false;
      this.hideLoadingState();
    }
  }

  showLoadingState() {
    let loader = document.getElementById('infinite-scroll-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.id = 'infinite-scroll-loader';
      loader.className = 'infinite-scroll-loader';
      loader.innerHTML = `
        <div class="loading-overlay gradient">
          <div class="loading__spinner">
            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
        </div>
      `;
      const gridParent = this.productGrid.parentNode;
      if (gridParent) {
        gridParent.appendChild(loader);
      }
    }
    loader.style.display = 'block';
  }

  hideLoadingState() {
    const loader = document.getElementById('infinite-scroll-loader');
    if (loader) {
      loader.style.display = 'none';
    }
  }

  // Método para reinicializar cuando cambian los filtros
  reset() {
    this.cleanup();
    this.currentPage = 1;
    this.loading = false;
    this.hasMorePages = true;
    
    // Esperar un momento para que facets.js termine de actualizar
    setTimeout(() => {
      this.setup();
    }, 200);
  }
}

customElements.define('infinite-scroll', InfiniteScroll);

// Reinicializar cuando facets.js actualiza el grid
let facetsMutationObserver = null;

function setupFacetsObserver() {
  // Limpiar observer anterior si existe
  if (facetsMutationObserver) {
    facetsMutationObserver.disconnect();
  }

  const gridContainer = document.getElementById('ProductGridContainer');
  if (!gridContainer) return;

  facetsMutationObserver = new MutationObserver((mutations) => {
    let gridWasReplaced = false;
    
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.target === gridContainer) {
        // Verificar si el grid fue reemplazado
        const newGrid = gridContainer.querySelector('#product-grid');
        if (newGrid) {
          gridWasReplaced = true;
        }
      }
    });

    if (gridWasReplaced) {
      // El grid fue actualizado por facets.js, reinicializar infinite scroll
      setTimeout(() => {
        const infiniteScrollElement = document.querySelector('infinite-scroll');
        if (infiniteScrollElement && infiniteScrollElement.reset) {
          infiniteScrollElement.reset();
        }
      }, 100);
    }
  });

  facetsMutationObserver.observe(gridContainer, {
    childList: true,
    subtree: false
  });
}

// Configurar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupFacetsObserver);
} else {
  setupFacetsObserver();
}

// También escuchar evento personalizado si facets.js lo dispara
document.addEventListener('productGridUpdated', () => {
  const infiniteScrollElement = document.querySelector('infinite-scroll');
  if (infiniteScrollElement && infiniteScrollElement.reset) {
    infiniteScrollElement.reset();
  }
});

